import type { AxiosRequestConfig, AxiosResponse } from 'axios'

import type { ApiClient } from 'clients/yogi-api'
import type {
  AskYogiAttachment,
  AskYogiCapability,
  AskYogiChartContext,
  AskYogiDashboardContext,
  AskYogiConnector,
  AskYogiConnectorState,
  AskYogiConnectorStateDto,
  AttachmentUploadDto,
  DuplicateConversationRequest,
  GenerateTemplateResponseDto,
  LinkPage,
  LinkPageStates,
  PromptShare,
  PromptShareDto,
  SavedPrompt,
  SavedPromptDto,
  SavedPromptPermission,
  SendMessageRequestBody,
  SkillContext,
} from 'features/ask-yogi/service/types'
import {
  toAskYogiAttachment,
  toAskYogiConnectorState,
  toPromptShare,
  toSavedPrompt,
} from 'features/ask-yogi/service/types'
import type {
  AgentCitationPost,
  AgentCitationPostsResponse,
  AskYogiChartResponse,
  AskYogiContextChip,
  ChartsV2Response,
  CitationFiltersResponse,
  ConversationData,
  ConversationsListResponse,
  ConversationSummaryWithShare,
  MessageFeedbackResponse,
  Personalization,
  StarterQuestions,
  TableFiltersResponse,
} from 'features/ask-yogi/types'
import type { Identifier } from 'features/charts-v2/types'
import type { ConversationResponse as ConversationResponseType } from 'src/types'

const ASK_YOGI_URL = '/ask-yogi'
const CONNECTORS_BASE_URL = '/client/settings/connectors'
const SAVED_PROMPTS_BASE_URL = '/client/saved-prompts'
const HUB_UUID_HEADER = 'X-Hub-UUID'
const LINK_PAGE_STATE = {
  charts: '/client/charts/yogiql/page-state',
  feedback: '/client/feedback/yogiql/page-state',
} as const satisfies Record<LinkPage, string>
const ASK_YOGI_EXTENDED_TIMEOUT_MS = 600000 // 10 minutes

const toHubConfig = (proj_uuid: string): AxiosRequestConfig => ({
  headers: { [HUB_UUID_HEADER]: proj_uuid },
})

/**
 * FastAPI reads a `list[...]` query param from REPEATED keys
 * (`record_id=1&record_id=2`). Axios's default serializer emits
 * `record_id[]=1`, which FastAPI does not parse — the param arrives empty.
 */
const REPEATED_QUERY_PARAMS = { indexes: null } as const

export class AskYogiService {
  constructor(private client: ApiClient) {}

  async fetchConnectors(proj_uuid: string): Promise<AskYogiConnectorState[]> {
    const { data } = await this.client.get<AskYogiConnectorStateDto[]>(
      CONNECTORS_BASE_URL,
      toHubConfig(proj_uuid),
    )
    return data.map(toAskYogiConnectorState)
  }

  async savePrompt(
    proj_uuid: string,
    body: { title: string; prompt_text: string },
  ): Promise<SavedPrompt> {
    const { data } = await this.client.post<SavedPromptDto>(
      SAVED_PROMPTS_BASE_URL,
      body,
      toHubConfig(proj_uuid),
    )
    return toSavedPrompt(data)
  }

  async fetchSavedPrompts(proj_uuid: string): Promise<SavedPrompt[]> {
    const { data } = await this.client.get<SavedPromptDto[]>(
      SAVED_PROMPTS_BASE_URL,
      toHubConfig(proj_uuid),
    )
    return data.map(toSavedPrompt)
  }

  async updatePrompt(
    proj_uuid: string,
    promptUuid: string,
    body: { title: string; prompt_text: string },
  ): Promise<SavedPrompt> {
    const { data } = await this.client.put<SavedPromptDto>(
      `${SAVED_PROMPTS_BASE_URL}/${promptUuid}`,
      body,
      toHubConfig(proj_uuid),
    )
    return toSavedPrompt(data)
  }

  async deletePrompt(proj_uuid: string, promptUuid: string): Promise<void> {
    await this.client.delete(
      `${SAVED_PROMPTS_BASE_URL}/${promptUuid}`,
      toHubConfig(proj_uuid),
    )
  }

  async generateTemplate(
    proj_uuid: string,
    source_message_uuid: string,
  ): Promise<string> {
    const { data } = await this.client.post<GenerateTemplateResponseDto>(
      `${SAVED_PROMPTS_BASE_URL}/generate-template`,
      { source_message_uuid },
      toHubConfig(proj_uuid),
    )
    return data.template
  }

  async fetchPromptShares(
    proj_uuid: string,
    promptUuid: string,
  ): Promise<PromptShare[]> {
    const { data } = await this.client.get<PromptShareDto[]>(
      `${SAVED_PROMPTS_BASE_URL}/${promptUuid}/shares`,
      toHubConfig(proj_uuid),
    )
    return data.map(toPromptShare)
  }

  async grantPromptShare(
    proj_uuid: string,
    promptUuid: string,
    recipientUserId: number,
    permission: SavedPromptPermission,
  ): Promise<void> {
    await this.client.put(
      `${SAVED_PROMPTS_BASE_URL}/${promptUuid}/shares/${recipientUserId}`,
      { permission },
      toHubConfig(proj_uuid),
    )
  }

  async revokePromptShare(
    proj_uuid: string,
    promptUuid: string,
    recipientUserId: number,
  ): Promise<void> {
    await this.client.delete(
      `${SAVED_PROMPTS_BASE_URL}/${promptUuid}/shares/${recipientUserId}`,
      toHubConfig(proj_uuid),
    )
  }

  async createConversation(params: {
    proj_uuid: string
  }): Promise<ConversationData> {
    const config = { params, timeout: ASK_YOGI_EXTENDED_TIMEOUT_MS }

    const { data } = await this.client.post<ConversationData>(
      `${ASK_YOGI_URL}/conversations`,
      {},
      config,
    )
    return data
  }

  /** Resolves with nothing: the route answers 204 with no body. */
  async deleteConversation(
    projectId: string,
    conversationUuid: string,
    customParams?: AxiosRequestConfig,
  ): Promise<void> {
    let config = { params: { proj_uuid: projectId } }
    if (customParams) config = { ...config, ...customParams }

    await this.client.delete(
      `${ASK_YOGI_URL}/conversations/${conversationUuid}`,
      config,
    )
  }

  async renameConversation(
    params: {
      proj_uuid: string
    },
    conversationUuid: string,
    newTitle: string,
    customParams?: AxiosRequestConfig,
  ): Promise<ConversationSummaryWithShare> {
    let config = { params }
    if (customParams) config = { ...config, ...customParams }

    const { data } = await this.client.patch<ConversationSummaryWithShare>(
      `${ASK_YOGI_URL}/conversations/${conversationUuid}`,
      { title: newTitle },
      config,
    )
    return data
  }

  async getConversations(
    params: {
      proj_uuid: string
    },
    customParams?: AxiosRequestConfig,
  ): Promise<ConversationsListResponse> {
    let config = { params }
    if (customParams) config = { ...config, ...customParams }

    const { data } = await this.client.get<ConversationsListResponse>(
      `${ASK_YOGI_URL}/conversations`,
      config,
    )
    return data
  }

  async getConversation(
    params: {
      proj_uuid: string
    },
    conversationUuid: string,
    customParams?: AxiosRequestConfig,
  ): Promise<ConversationData> {
    let config = { params }
    if (customParams) config = { ...config, ...customParams }

    const { data } = await this.client.get<ConversationData>(
      `${ASK_YOGI_URL}/conversations/${conversationUuid}`,
      config,
    )
    return data
  }

  async getSharedConversation(
    params: {
      proj_uuid: string
    },
    conversationUuid: string,
    customParams?: AxiosRequestConfig,
  ): Promise<ConversationData> {
    let config = { params }
    if (customParams) config = { ...config, ...customParams }
    const { data } = await this.client.get<ConversationData>(
      `${ASK_YOGI_URL}/shared-conversations/${conversationUuid}`,
      config,
    )
    return data
  }

  /**
   * Post one turn to an EXISTING conversation. The conversation is a real
   * resource here, so `conversationUuid` is required — callers create it
   * first (`createConversation`) and only then send.
   */
  sendMessage(
    params: {
      proj_uuid: string
    },
    messageData: {
      message: string | null
    },
    conversationUuid: string,
    capabilities?: AskYogiCapability[],
    connectors?: AskYogiConnector[],
    customParams?: AxiosRequestConfig,
    attachmentUuids?: string[],
    chartContext?: AskYogiChartContext,
    dashboardContext?: AskYogiDashboardContext,
    contextChip?: AskYogiContextChip | null,
    skills?: string[],
    skillContext?: SkillContext,
  ): Promise<AxiosResponse<{ conversation: ConversationResponseType }>> {
    let config = { params, timeout: ASK_YOGI_EXTENDED_TIMEOUT_MS }
    if (customParams) config = { ...config, ...customParams }

    const requestBody: SendMessageRequestBody = {
      message: messageData.message,
    }

    if (attachmentUuids && attachmentUuids.length > 0) {
      requestBody.attachment_uuids = attachmentUuids
    }

    if (capabilities && capabilities.length > 0) {
      requestBody.capabilities = capabilities
    }
    if (connectors && connectors.length > 0) {
      requestBody.connectors = connectors
    }
    // Sent whenever the caller is the charts page, even with no chart drawn:
    // its PRESENCE is what tells the backend which surface this is, and an
    // empty charts page is still the charts page.
    if (chartContext) {
      requestBody.chart_context = chartContext
    }
    if (dashboardContext) {
      requestBody.dashboard_context = dashboardContext
    }
    if (contextChip) {
      requestBody.context_chip = contextChip
    }
    if (skills && skills.length > 0) {
      requestBody.skills = skills
    }
    if (skillContext && Object.keys(skillContext).length > 0) {
      requestBody.skill_context = skillContext
    }

    return this.client.post(
      `${ASK_YOGI_URL}/conversations/${conversationUuid}/messages`,
      requestBody,
      config,
    )
  }

  async uploadAttachment(
    projUuid: string,
    file: File,
  ): Promise<AskYogiAttachment> {
    const uploadForm = new FormData()
    uploadForm.append('file', file)

    const { data } = await this.client.post<AttachmentUploadDto>(
      `${ASK_YOGI_URL}/attachments`,
      uploadForm,
      { params: { proj_uuid: projUuid } },
    )
    return toAskYogiAttachment(data)
  }

  /**
   * Ask one in-progress run to stop.
   *
   * Returns nothing on purpose: the response body only echoes the run the
   * request was recorded for. Whether the turn has actually stopped is not
   * knowable at this point — the run honors the flag at its next turn boundary,
   * which may be seconds away while an in-flight query finishes — so the
   * terminal `cancelled` status arrives on the conversation's event stream, not
   * here.
   *
   * Mirrors `sendMessage`'s config shape: the route is mounted at the app root
   * alongside the other `/ask-yogi/...` endpoints (NOT under `/client`), and it
   * authorizes through the same dependency the message route uses, which reads
   * the project from a `proj_uuid` query param rather than the `X-Hub-UUID`
   * header the `/client/...` calls send.
   */
  async stopRun(params: { proj_uuid: string }, runUuid: string): Promise<void> {
    await this.client.post(`${ASK_YOGI_URL}/runs/${runUuid}/stop`, undefined, {
      params,
    })
  }

  async getStarterQuestions(projectId: string): Promise<StarterQuestions> {
    const config = { params: { proj_uuid: projectId, num_questions: 3 } }
    const { data } = await this.client.get<StarterQuestions>(
      `${ASK_YOGI_URL}/starter-questions`,
      config,
    )
    return data
  }

  async fetchChart(
    proj_uuid: string,
    identifier: Identifier,
    customParams?: AxiosRequestConfig,
  ): Promise<ChartsV2Response> {
    let config = { params: { proj_uuid } }
    if (customParams) {
      config = { ...config, ...customParams }
    }
    const { data } = await this.client.post<ChartsV2Response>(
      '/charts/ask-yogi',
      { identifier: identifier },
      config,
    )
    return data
  }

  async fetchChartByRequest(
    proj_uuid: string,
    request: Record<string, unknown>,
    customParams?: AxiosRequestConfig,
  ): Promise<AskYogiChartResponse> {
    let config = { params: { proj_uuid } }
    if (customParams) {
      config = { ...config, ...customParams }
    }
    const { data } = await this.client.post<AskYogiChartResponse>(
      `${ASK_YOGI_URL}/charts/data`,
      request,
      config,
    )
    return data
  }

  /**
   * Batch-fetch citation-preview posts for the agent features. Replaces the
   * per-citation `GET /post/id` fan-out: pass every citation id for a
   * message (or a single id, for the benchmarking / issue-detection
   * popovers) and get one response. Ids not found or outside the project
   * are omitted from `posts`.
   */
  async getCitationPosts(
    projectId: string,
    postIds: number[],
  ): Promise<AgentCitationPost[]> {
    if (postIds.length === 0) return []
    const { data } = await this.client.get<AgentCitationPostsResponse>(
      `${ASK_YOGI_URL}/posts`,
      {
        params: { proj_uuid: projectId, post_id: postIds },
        paramsSerializer: REPEATED_QUERY_PARAMS,
      },
    )
    return data.posts
  }

  /**
   * Same endpoint as `getCitationPosts`, plus the optional sentence-grain
   * half of the request: `postTextIds` a verbatim's citation named, to
   * resolve a theme/sentiment for. Returns the full response (posts AND
   * post_text_themes) rather than just `posts`, so this is a separate
   * method rather than an overload of `getCitationPosts` — existing callers
   * of that method only want the post array and stay unaffected.
   */
  async getCitationPostsWithThemes(
    projectId: string,
    postIds: number[],
    postTextIds: number[] = [],
  ): Promise<AgentCitationPostsResponse> {
    if (postIds.length === 0) return { posts: [], post_text_themes: [] }
    const { data } = await this.client.get<AgentCitationPostsResponse>(
      `${ASK_YOGI_URL}/posts`,
      {
        params: {
          proj_uuid: projectId,
          post_id: postIds,
          post_text_id: postTextIds,
        },
        paramsSerializer: REPEATED_QUERY_PARAMS,
      },
    )
    return data
  }

  /**
   * Record thumbs-up/down on one message. Errors are NOT swallowed: a 404
   * means the message does not belong to that conversation (or the
   * conversation not to the project), and the caller surfaces it.
   */
  async sendFeedback(
    projectId: string,
    conversationUuid: string,
    messageUuid: string,
    request: {
      is_positive: boolean
      issue_reason?: string | null
      additional_details?: string | null
    },
  ): Promise<MessageFeedbackResponse> {
    const config = { params: { proj_uuid: projectId } }

    const { data } = await this.client.post<MessageFeedbackResponse>(
      `${ASK_YOGI_URL}/conversations/${conversationUuid}/messages/${messageUuid}/feedback`,
      request,
      config,
    )
    return data
  }

  async getPersonalization(projectId: string): Promise<Personalization> {
    const config = { params: { proj_uuid: projectId } }

    const { data } = await this.client.get<Personalization>(
      `${ASK_YOGI_URL}/user-prompt`,
      config,
    )
    return data
  }

  async updatePersonalization(
    projectId: string,
    personalizationData: Personalization,
  ): Promise<{ success: boolean }> {
    const config = { params: { proj_uuid: projectId } }

    const { data } = await this.client.put<{ success: boolean }>(
      `${ASK_YOGI_URL}/user-prompt`,
      personalizationData,
      config,
    )
    return data
  }

  async getConversationTitle(
    projectId: string,
    conversation_uuid: string,
  ): Promise<ConversationSummaryWithShare> {
    const config = { params: { proj_uuid: projectId } }

    const { data } = await this.client.post<ConversationSummaryWithShare>(
      `${ASK_YOGI_URL}/conversations/${conversation_uuid}/generate-title`,
      undefined,
      config,
    )
    return data
  }

  async shareConversation(
    projectId: string,
    conversationUuid: string,
    shared: boolean,
  ): Promise<ConversationSummaryWithShare> {
    const config = { params: { proj_uuid: projectId } }

    const { data } = await this.client.patch<ConversationSummaryWithShare>(
      `${ASK_YOGI_URL}/conversations/${conversationUuid}`,
      { is_shared: shared },
      config,
    )
    return data
  }

  async duplicateConversation(
    projectId: string,
    conversationUuid: string,
    beforeMessageUuid?: string,
  ): Promise<ConversationData> {
    const config = { params: { proj_uuid: projectId } }

    const requestBody: DuplicateConversationRequest = beforeMessageUuid
      ? { before_message_uuid: beforeMessageUuid }
      : {}

    const { data } = await this.client.post<ConversationData>(
      `${ASK_YOGI_URL}/conversations/${conversationUuid}/duplicate`,
      requestBody,
      config,
    )
    return data
  }

  async getCitationFilters(
    projectId: string,
    conversationUuid: string,
    recordIds: string[],
    customParams?: AxiosRequestConfig,
  ): Promise<CitationFiltersResponse> {
    let config: AxiosRequestConfig = {
      params: {
        proj_uuid: projectId,
        record_id: recordIds
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id)),
      },
      paramsSerializer: REPEATED_QUERY_PARAMS,
    }
    if (customParams) config = { ...config, ...customParams }

    const { data } = await this.client.get<CitationFiltersResponse>(
      `${ASK_YOGI_URL}/conversations/${conversationUuid}/citations`,
      config,
    )
    return data
  }

  async getTableFilters(
    projectId: string,
    conversationUuid: string,
    resultIds: string[],
    customParams?: AxiosRequestConfig,
  ): Promise<TableFiltersResponse> {
    let config: AxiosRequestConfig = {
      params: {
        proj_uuid: projectId,
        result_id: resultIds,
      },
      paramsSerializer: REPEATED_QUERY_PARAMS,
    }
    if (customParams) config = { ...config, ...customParams }

    const { data } = await this.client.get<TableFiltersResponse>(
      `${ASK_YOGI_URL}/conversations/${conversationUuid}/tables`,
      config,
    )
    return data
  }

  /** Stores a "View in ..." link's target page state through that page's
   *  page-state route, and answers the key the link opens. */
  async saveLinkState<P extends LinkPage>(
    proj_uuid: string,
    page: P,
    state: LinkPageStates[P],
  ): Promise<string> {
    const { data } = await this.client.post<{ key: string }>(
      LINK_PAGE_STATE[page],
      { state },
      toHubConfig(proj_uuid),
    )
    return data.key
  }
}
